import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  TimeIntervalEditForm,
  TimeIntervalEditInitialValues,
  TimeIntervalComponents,
  TimeIntervalAdditionalParams,
  TimeIntervalFormOptions,
  onTimeIntervalFormSaved,
  ITimeIntervalFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedTimeInterval, ISharedPatchTimeInterval, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchTimeInterval, patchTimeInterval, getTimeIntervalUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedTimeInterval> {}

const TimeIntervalFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchTimeInterval(id).then(time_interval => setValue(time_interval));
  }, [id, setValue, ]);

  const param_props: ITimeIntervalFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && TimeIntervalEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && TimeIntervalFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchTimeInterval, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchTimeInterval) => value && save(() => patchTimeInterval(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onTimeIntervalFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.TimeInterval, value)}
    >
      <AtlasForm<ISharedPatchTimeInterval>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={TimeIntervalComponents}
        additionalParams={TimeIntervalAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={TimeIntervalEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('TIME_INTERVAL')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const TimeIntervalFormEditView = React.memo(withEdit(TimeIntervalFormEdit, getTimeIntervalUpdateSchema));
